import React from 'react';
import { useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Mail, Phone, Globe, Star, PlusCircle } from 'lucide-react';
import { SERVICE_PROVIDERS } from '../ServiceProvidersData.jsx'; // Assuming this is where your data is

const ServiceDetailPage = () => {
    const { id } = useParams();
    const provider = SERVICE_PROVIDERS.find(p => p.id === id);

    if (!provider) {
        return <div className="text-center py-12">Service provider not found.</div>;
    }

    const { name, description, types, contactInfo, rating } = provider;

    // Determine banner image based on service provider
    const getBannerImage = () => {
        if (id === "trudbooks-accounting-1") {
            return "/truD.avif";
        }
        return "https://via.placeholder.com/1200x400";
    };

    return (
        <div className="bg-gray-50">
            <div className="container mx-auto px-4 py-8">
                {/* Header with banner image */}
                <div className="relative h-64 md:h-80 lg:h-96 rounded-lg overflow-hidden mb-8">
                    <img
                        src={getBannerImage()}
                        alt={`${name} banner`}
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-40 flex items-end p-8">
                        <h1 className="text-white text-3xl md:text-5xl font-bold">{name}</h1>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Left Column */}
                    <div className="lg:col-span-2 space-y-8">
                        <Card>
                            <CardHeader>
                                <div className="flex justify-between items-center">
                                    <CardTitle className="text-[#1a4a1a]">About {name}</CardTitle>
                                    <div className="flex items-center gap-4">
                                        {rating && (
                                            <div className="flex items-center gap-1 text-lg">
                                                <Star className="text-yellow-500 fill-yellow-500" />
                                                <span className="font-semibold">{rating}</span>
                                            </div>
                                        )}
                                        {/* <Button variant="outline">
                                            <PlusCircle className="mr-2 h-4 w-4" /> Add Review
                                        </Button> */}
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent>
                                <div className="prose max-w-none prose-headings:text-[#1a4a1a] prose-a:text-[#1a4a1a] prose-a:no-underline hover:prose-a:underline prose-strong:text-[#1a4a1a] prose-p:text-gray-700">
                                    <ReactMarkdown>{description}</ReactMarkdown>
                                </div>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle className="text-[#1a4a1a]">Services Offered</CardTitle>
                            </CardHeader>
                            <CardContent className="flex flex-wrap gap-2">
                                {types.map((type, index) => (
                                    <Badge key={index} variant="secondary" className="text-lg py-1 px-3">{type}</Badge>
                                ))}
                            </CardContent>
                        </Card>

                        {/*} <Card>
                            <CardHeader>
                                <CardTitle>Write a Review</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="flex items-center gap-2">
                                    {[...Array(5)].map((_, i) => (
                                        <Star key={i} className="text-gray-300 h-8 w-8 cursor-pointer" />
                                    ))}
                                </div>
                                <Textarea placeholder="Share your experience..." rows={5} />
                                <Button>Submit Review</Button>
                            </CardContent>
                        </Card> */}
                    </div>

                    {/* Right Column */}
                    <div className="space-y-8">
                        <Card className="border-2 border-green-100 shadow-lg">
                            <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50 border-b border-green-200">
                                <CardTitle className="text-[#1a4a1a] flex items-center gap-2">
                                    <div className="p-2 bg-green-100 rounded-full">
                                        <Mail className="h-5 w-5 text-green-600" />
                                    </div>
                                    Contact Information
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-6 pt-6">
                                {contactInfo.email && (
                                    <a href={`mailto:${contactInfo.email}`} className="flex items-center gap-4 p-3 rounded-lg hover:bg-green-50 transition-colors duration-200 group">
                                        <div className="p-2 bg-blue-100 rounded-full group-hover:bg-blue-200 transition-colors duration-200">
                                            <Mail className="h-5 w-5 text-blue-600" />
                                        </div>
                                        <div className="flex-1">
                                            <p className="text-sm text-gray-500 font-medium">Email</p>
                                            <p className="text-gray-700 group-hover:text-green-700 transition-colors duration-200">{contactInfo.email}</p>
                                        </div>
                                    </a>
                                )}
                                {contactInfo.phone && (
                                    <div className="flex items-center gap-4 p-3 rounded-lg hover:bg-green-50 transition-colors duration-200 group">
                                        <div className="p-2 bg-green-100 rounded-full group-hover:bg-green-200 transition-colors duration-200">
                                            <Phone className="h-5 w-5 text-green-600" />
                                        </div>
                                        <div className="flex-1">
                                            <p className="text-sm text-gray-500 font-medium">Phone</p>
                                            <p className="text-gray-700">{contactInfo.phone}</p>
                                        </div>
                                    </div>
                                )}
                                {contactInfo.website && (
                                    <a href={contactInfo.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 p-3 rounded-lg hover:bg-green-50 transition-colors duration-200 group">
                                        <div className="p-2 bg-purple-100 rounded-full group-hover:bg-purple-200 transition-colors duration-200">
                                            <Globe className="h-5 w-5 text-purple-600" />
                                        </div>
                                        <div className="flex-1">
                                            <p className="text-sm text-gray-500 font-medium">Website</p>
                                            <p className="text-gray-700 group-hover:text-green-700 transition-colors duration-200">Visit Website</p>
                                        </div>
                                    </a>
                                )}
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ServiceDetailPage; 